import { createContext, useContext, useEffect, useRef, useState } from "react";
import { getApi } from "../../services/api/globalApi";

const PendingOrdersContext = createContext({ count: 0, updateId: 0 });
const POLL_INTERVAL = 20000;

export function PendingOrdersProvider({ children, enabled = true }) {
  const [count, setCount] = useState(0);
  const [updateId, setUpdateId] = useState(0);
  const previousCount = useRef(null);
  const audioContext = useRef(null);

  useEffect(() => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return undefined;

    // Browsers permit audio only after a user gesture. Until then notifications are visual.
    const unlockAudio = () => {
      try {
        audioContext.current = new AudioContext();
        audioContext.current.resume().catch(() => {});
      } catch (_) {
        // Audio is optional; a blocked device must not affect order polling.
      }
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
    };

    document.addEventListener("pointerdown", unlockAudio, { once: true });
    document.addEventListener("keydown", unlockAudio, { once: true });
    return () => {
      document.removeEventListener("pointerdown", unlockAudio);
      document.removeEventListener("keydown", unlockAudio);
      audioContext.current?.close().catch(() => {});
    };
  }, []);

  useEffect(() => {
    if (!enabled) { setCount(0); previousCount.current = null; return undefined; }
    let active = true;
    let timer;

    const playDing = async () => {
      const context = audioContext.current;
      if (!context) return;
      try {
        if (context.state !== "running") await context.resume();
        if (!active) return;
        const start = context.currentTime;
        const gain = context.createGain();
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.11, start + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.36);
        gain.connect(context.destination);
        [880, 1320].forEach((frequency, index) => {
          const tone = context.createOscillator();
          tone.type = "sine";
          tone.frequency.value = frequency;
          tone.connect(gain);
          tone.start(start + index * 0.065);
          tone.stop(start + 0.36);
          tone.onended = () => tone.disconnect();
        });
        window.setTimeout(() => gain.disconnect(), 450);
      } catch (_) {
        // The browser or output device can reject playback without affecting the UI.
      }
    };

    const poll = async () => {
      try {
        const response = await getApi({ path: "admin/reports/totalProccessingOrdersCount" });
        if (!active) return;
        const nextCount = Number(response.data.result);
        if (!Number.isFinite(nextCount) || nextCount < 0) return;
        if (previousCount.current !== null && nextCount > previousCount.current) {
          setUpdateId((id) => id + 1);
          playDing();
        }
        previousCount.current = nextCount;
        setCount(nextCount);
      } catch (error) {
        if (active) console.error("Error fetching pending order count:", error);
      } finally {
        if (active) timer = window.setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [enabled]);

  return (
    <PendingOrdersContext.Provider value={{ count, updateId }}>
      {children}
    </PendingOrdersContext.Provider>
  );
}

export function usePendingOrders() {
  return useContext(PendingOrdersContext);
}
